import shadows from '@material-ui/core/styles/shadows'
import { fade } from '@material-ui/core/styles/colorManipulator'
import createPalette from '@material-ui/core/styles/createPalette'
import createMuiTheme from '@material-ui/core/styles/createMuiTheme'

const fontSize = 13
const htmlFontSize = 16
const coef = fontSize / 13

function pxToRem(value: number) {
  return `${value / htmlFontSize * coef}rem`
}

const notchSize = '5px'
const textColor = '#27354F'
const primaryColor = '#1EEDB5'
const tertiaryColor = '#3D7CFF'

const topNotch = `
  polygon(
    0% ${notchSize},
    ${notchSize} 0%,
    calc(100% - ${notchSize}) 0%,
    100% ${notchSize},
    100% 100%,
    0% 100%
  )
`

const bottomNotch = `
  polygon(
    0% 0%,
    100% 0%,
    100% calc(100% - ${notchSize}),
    calc(100% - ${notchSize}) 100%,
    ${notchSize} 100%,
    0% calc(100% - ${notchSize})
  )
`

const palette = createPalette({
  accent: {
    light: '#27354F',
    main: '#27354F',
    dark: '#27354F',
    contrastText: '#fff'
  },
  tertiary: {
    main: tertiaryColor,
    light: tertiaryColor,
    dark: tertiaryColor,
    contrastText: '#fff'
  },
})

const theme = createMuiTheme({
  notchSize,
  borderRadius: 5,
  palette: {
    ...palette,
    background: {
      default: '#F6F8F9',
    },
    primary: {
      main: primaryColor,
      contrastText: textColor
    },
    secondary: {
      main: '#E2E7EC',
      contrastText: '#000'
    },
    error: {
      light: '#e57373',
      main: '#FF5C5C',
      dark: '#d32f2f'
    },
    text: {
      secondary: fade(textColor, .5)
    },
    grey: {
      50: '#F1F4F9',
      100: '#E2E7EC',
      200: '#D2D7DC',
      600: '#B2B7BC',
      800: '#27354F',
      900: '#111721'
    }
  },
  typography: {
    fontSize,
    fontFamily: '"Flama", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'initial'
    },
    body1: {
      color: textColor,
      fontWeight: 500,
      fontSize: pxToRem(fontSize)
    },
    body2: {
      color: textColor,
      fontSize: pxToRem(fontSize),
    },
    subtitle1: {
      color: textColor,
      fontSize: pxToRem(16)
    },
    h5: {
      color: textColor,
      fontWeight: 500,
      fontSize: pxToRem(24)
    },
    h6: {
      color: textColor,
    },
    caption: {
      color: textColor,
      fontWeight: 500,
      fontSize: pxToRem(11)
    },
    h4: {
      color: textColor,
      fontWeight: 500,
      fontSize: pxToRem(18)
    },
    h3: {
      fontSize: pxToRem(11),
      color: fade(textColor, .5),
    },
    h2: {
      fontSize: pxToRem(11),
      color: fade('#fff', .5),
    },
    h1: {
      color: textColor,
      fontSize: pxToRem(9)
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 4
      },
      contained: {
        boxShadow: shadows[0]
      },
      containedSecondary: {
        color: textColor,
        backgroundColor: '#E2E7EC'
      }
    },
    MuiTabs: {
      indicator: {
        backgroundColor: primaryColor
      }
    },
    MuiCardHeader: {
      title: {
        fontSize: pxToRem(16)
      },
      subheader: {
        fontSize: pxToRem(13)
      }
    },
    MuiListItem: {
      root: {
        paddingTop: 11,
        paddingBottom: 11,
      }
    },
    MuiExpansionPanel: {
      root: {
        backgroundColor: 'transparent',
        '&$expanded': {
          margin: 0,
        },
        '&::before': {
          display: 'none',
        }
      },
      expanded: {},
    },
    MuiExpansionPanelDetails: {
      root: {
        padding: 0,
        display: 'block',
      }
    }
  },
  components: {
    GameLayout: {
      headerMinHeight: 60,
      headerMaxHeight: 140,
    },
    GameChatBar: {
      height: 40,
    },
    PlayerAvatar: {
      backgroundColor: palette.secondary.main,
    },
    BudgetIndicator: {
      default: {
        root: {
          border: 'none',
        },
        progress: {
          backgroundColor: tertiaryColor,
        },
        text: {
          color: palette.common.white,
        },
      },
      error: {
        root: {
          border: `1px solid ${palette.error.main}`,
        },
        progress: {
          backgroundColor: palette.error.main,
        },
        text: {
          color: palette.error.main,
        },
      },
    },
    UserInfo: {
      avatar: {
        width: 80,
        height: 80,
      },
    },
    PremiumMembership: {
      backgroundColor: {
        default: '#3d7cff',
        silver: '#f6f8f9',
        gold: '#eaca00',
      },
      color: {
        default: 'white',
        silver: textColor,
        gold: textColor,
      },
    },
    MatchCard: {
      header: {
        borderTopLeftRadius: 'inherit',
        borderTopRightRadius: 'inherit',
        clipPath: bottomNotch,
        '-webkit-clip-path': bottomNotch
      },
      footer: {
        borderBottomLeftRadius: 'inherit',
        borderBottomRightRadius: 'inherit',
        clipPath: topNotch,
        '-webkit-clip-path': topNotch
      }
    }
  },
})

export default theme
